import React, { useEffect, useState } from 'react';
import { Avatar, Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MainLayout from '@/components/MainLayout';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { downloadCsv } from '@/lib/download';
import { toast } from '@/lib/toast';

const initials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

const ROLE_LABELS = {
  superadmin: 'Superadmin',
  owner_admin: 'Owner',
  staff: 'Staff',
  trainer: 'Trainer',
  member: 'Member',
};

const StaffPage = () => {
  const { isOwner } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api
      .get('/users')
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error('Failed to load staff'))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadCsv('/users/export', 'staff');
    } catch {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <MainLayout title="Staff" badge={loading ? undefined : users.length}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
          <Typography color="text.secondary" variant="body2">
            Team &amp; roles. New staff accounts are created via the auth/register endpoint.
          </Typography>
          {isOwner && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-staff-button"
              sx={{ flexShrink: 0, borderColor: 'divider', color: 'text.secondary' }}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          )}
        </Stack>

        {loading ? (
          <Skeleton variant="rounded" height={320} />
        ) : (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            {users.map((u, idx) => (
              <Stack
                key={u.user_id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderBottom: idx === users.length - 1 ? 0 : 1,
                  borderColor: 'divider',
                }}
              >
                <Avatar
                  src={u.avatar_url || undefined}
                  alt={u.name}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 13,
                    fontWeight: 600,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.16),
                    color: 'primary.main',
                  }}
                >
                  {initials(u.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {u.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {u.email}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {ROLE_LABELS[u.role] || u.role}
                </Typography>
              </Stack>
            ))}
            {users.length === 0 && (
              <Typography align="center" color="text.secondary" variant="body2" sx={{ py: 6 }}>
                No staff accounts yet.
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </MainLayout>
  );
};

export default StaffPage;
